/**
 * ============================================================================
 * TabsNodeView — NodeView React do bloco `tabs` (estilo GitBook). Renderiza
 *                uma barra de abas nomeáveis + reordenáveis via drag e um
 *                painel de conteúdo (NodeViewContent). Cada `tabPanel` aceita
 *                QUALQUER bloco.
 *
 * Arquitetura de visibilidade (v2):
 *   Cada `tabPanel` carrega um attr `active` refletido como `data-active` no
 *   DOM (schema). CSS esconde `[data-active="false"]`. Toda troca de aba, add,
 *   duplicate, delete e reorder despacha UMA transação atômica que:
 *     1) reordena/insere/remove painéis conforme a ação;
 *     2) recalcula `active` de TODOS os filhos (apenas o novo ativo = true);
 *     3) atualiza `activeIndex` do próprio `tabs`.
 *   Não há mais toggle imperativo de `style.display` — fim do bug em que todas
 *   as abas mostravam o mesmo conteúdo.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v2.0.2 - Corrige botão "..." (menu de ações da aba) que
 *            não abria: DropdownMenu agora é controlado por estado local,
 *            trigger bloqueia pointerdown para o PointerSensor externo (dnd-kit
 *            do KbTiptapEditor), Content usa modal={false} +
 *            onCloseAutoFocus.preventDefault para o ProseMirror não roubar o
 *            foco e fechar o menu instantaneamente.
 *   RELEASE: 2026-07-08 v2.0.1 - Corrige cálculo de posições ProseMirror ao
 *            ativar abas. Substitui `setNodeAttribute` em filho por replace
 *            atômico dos painéis, evitando TransformError ao clicar nas tabs.
 *   RELEASE: 2026-07-08 v2.0.0 - Visibilidade declarativa via attr `active`
 *            (elimina toggle DOM frágil). Auto-migração de docs antigos.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (add/rename/duplicate/delete +
 *            drag-reorder via @dnd-kit).
 * ============================================================================
 */
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from "@tiptap/react";
import { Fragment, type Node as PMNode } from "@tiptap/pm/model";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, X, Pencil, Copy } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface PanelInfo {
  index: number;
  label: string;
  active: boolean;
}

/** Garante que índices vindos do DOM/atributos nunca escapem do range real. */
function clampPanelIndex(index: number, count: number) {
  if (count <= 0) return 0;
  return Math.max(0, Math.min(index, count - 1));
}

/** Converte os filhos do nó `tabs` para array sem perder conteúdo/marks. */
function panelChildrenOf(sourceNode: PMNode) {
  const children: PMNode[] = [];
  sourceNode.forEach((child) => children.push(child));
  return children;
}

/** Replica os painéis preservando conteúdo, mas deixando apenas um ativo. */
function panelsWithSingleActive(children: PMNode[], targetIndex: number) {
  const safeTarget = clampPanelIndex(targetIndex, children.length);
  return children.map((child, index) =>
    child.type.create(
      { ...child.attrs, active: index === safeTarget },
      child.content,
      child.marks,
    ),
  );
}

/** Resolve o índice ativo efetivo: attr do painel tem prioridade, activeIndex é fallback. */
function resolveActiveIndex(children: PMNode[], fallbackIndex: number) {
  const attrIndex = children.findIndex((child) => Boolean(child.attrs.active));
  return clampPanelIndex(attrIndex >= 0 ? attrIndex : fallbackIndex, children.length);
}

export function TabsNodeView({ editor, node, getPos }: NodeViewProps) {
  const editable = editor?.isEditable ?? true;
  const activeIndex = Math.max(
    0,
    Math.min(Number(node.attrs.activeIndex ?? 0), Math.max(0, node.childCount - 1)),
  );
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const panels: PanelInfo[] = useMemo(() => {
    const list: PanelInfo[] = [];
    node.forEach((child, _off, i) => {
      list.push({
        index: i,
        label: String(child.attrs.label ?? `Aba ${i + 1}`),
        active: Boolean(child.attrs.active),
      });
    });
    return list;
  }, [node]);

  const visualActiveIndex = useMemo(() => {
    const attrIndex = panels.findIndex((p) => p.active);
    return attrIndex >= 0 ? attrIndex : activeIndex;
  }, [panels, activeIndex]);

  // ---------------- helpers de mutação estrutural ----------------
  const getTabsPos = useCallback(() => {
    if (typeof getPos !== "function") return null;
    let pos: unknown = null;
    try {
      pos = getPos();
    } catch {
      // NodeViews removidos/recriados podem deixar getPos temporariamente inválido.
      return null;
    }
    if (typeof pos !== "number") return null;

    // Validação defensiva: NodeViews podem ficar obsoletos após transações.
    const currentNode = editor.state.doc.nodeAt(pos);
    if (!currentNode || currentNode.type.name !== "tabs") return null;
    return pos;
  }, [editor, getPos]);

  const getPanelNodePos = useCallback((index: number) => {
    const base = getTabsPos();
    const currentNode = base == null ? null : editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs" || index < 0 || index >= currentNode.childCount) return null;
    let cursor = base + 1;
    for (let i = 0; i < index; i++) cursor += currentNode.child(i).nodeSize;
    return cursor;
  }, [editor, getTabsPos]);

  const preserveScroll = useCallback((fn: () => void) => {
    const el = editor.view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
    const top = el?.scrollTop ?? 0;
    const left = el?.scrollLeft ?? 0;
    fn();
    const apply = () => {
      if (!el) return;
      el.scrollTop = top;
      el.scrollLeft = left;
    };
    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }, [editor]);

  /**
   * Substitui somente os filhos do bloco `tabs`, preservando o nó container.
   * Evita `setNodeAttribute` em posições de filhos — a origem do runtime error.
   */
  const replaceTabsChildren = useCallback((base: number, tabsNode: PMNode, nextChildren: PMNode[], nextActive: number, addToHistory: boolean) => {
    const safeActive = clampPanelIndex(nextActive, nextChildren.length);
    const from = base + 1;
    const to = base + tabsNode.nodeSize - 1;
    const tr = editor.state.tr
      .replaceWith(from, to, Fragment.fromArray(panelsWithSingleActive(nextChildren, safeActive)))
      .setNodeAttribute(base, "activeIndex", safeActive)
      .setMeta("addToHistory", addToHistory);

    if (tr.docChanged) editor.view.dispatch(tr);
  }, [editor]);

  /**
   * Auto-migração: se o doc antigo não tem NENHUM painel com active=true,
   * sincroniza baseado em activeIndex ao montar. Também garante consistência
   * quando activeIndex externo mudar.
   */
  useEffect(() => {
    if (node.childCount === 0) return;
    const targetActive = resolveActiveIndex(panelChildrenOf(node), activeIndex);
    const alreadySynced = panels.every((panel) => panel.active === (panel.index === targetActive));
    if (alreadySynced && Number(node.attrs.activeIndex ?? 0) === targetActive) return;

    let cancelled = false;
    const frame = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const base = getTabsPos();
      if (base == null) return;
      const currentNode = editor.state.doc.nodeAt(base);
      if (!currentNode || currentNode.type.name !== "tabs" || currentNode.childCount === 0) return;
      replaceTabsChildren(base, currentNode, panelChildrenOf(currentNode), targetActive, false);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [editor, node, activeIndex, panels, getTabsPos, replaceTabsChildren]);

  const setActive = useCallback((idx: number) => {
    const base = getTabsPos();
    if (base == null) return;
    const currentNode = editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs" || currentNode.childCount === 0) return;

    const children = panelChildrenOf(currentNode);
    const nextActive = clampPanelIndex(idx, children.length);
    const alreadySynced =
      Number(currentNode.attrs.activeIndex ?? 0) === nextActive &&
      children.every((child, index) => Boolean(child.attrs.active) === (index === nextActive));
    if (alreadySynced) return;

    replaceTabsChildren(base, currentNode, children, nextActive, false);
  }, [editor, getTabsPos, replaceTabsChildren]);

  const addTab = () => {
    const base = getTabsPos();
    if (base == null) return;
    const currentNode = editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs") return;

    const children = panelChildrenOf(currentNode);
    const newIdx = children.length; // índice do novo painel
    const newPanel = editor.schema.nodes.tabPanel.create(
      { label: `Aba ${newIdx + 1}`, active: true },
      editor.schema.nodes.paragraph.create(),
    );
    preserveScroll(() => {
      replaceTabsChildren(base, currentNode, [...children, newPanel], newIdx, true);
    });
  };

  const renameTab = (i: number, next: string) => {
    const panelNodePos = getPanelNodePos(i);
    if (panelNodePos == null) return;
    const panelNode = editor.state.doc.nodeAt(panelNodePos);
    if (!panelNode || panelNode.type.name !== "tabPanel") return;
    editor.view.dispatch(
      editor.state.tr.setNodeAttribute(panelNodePos, "label", next.trim() || `Aba ${i + 1}`),
    );
  };

  const duplicateTab = (i: number) => {
    const base = getTabsPos();
    if (base == null) return;
    const currentNode = editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs") return;
    const children = panelChildrenOf(currentNode);
    if (i < 0 || i >= children.length) return;

    const panelNode = children[i];
    const clone = panelNode.type.create({ ...panelNode.attrs, active: true }, panelNode.content, panelNode.marks);
    const nextChildren = [...children.slice(0, i + 1), clone, ...children.slice(i + 1)];
    preserveScroll(() => {
      replaceTabsChildren(base, currentNode, nextChildren, i + 1, true);
    });
  };

  const deleteTab = (i: number) => {
    const base = getTabsPos();
    if (base == null) return;
    const currentNode = editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs") return;
    const children = panelChildrenOf(currentNode);
    if (children.length <= 1 || i < 0 || i >= children.length) return; // sempre pelo menos 1 aba

    const currentActive = resolveActiveIndex(children, Number(currentNode.attrs.activeIndex ?? 0));
    const nextChildren = children.filter((_, index) => index !== i);
    const nextActive = currentActive === i
      ? Math.min(i, nextChildren.length - 1)
      : currentActive > i
        ? currentActive - 1
        : currentActive;

    preserveScroll(() => {
      replaceTabsChildren(base, currentNode, nextChildren, nextActive, true);
    });
  };

  /**
   * Reordena os painéis substituindo o conteúdo COMPLETO do tabs por um
   * Fragment na nova ordem. Simples, atômico e evita malabarismo com steps
   * do ProseMirror.
   */
  const reorderTabs = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    const base = getTabsPos();
    if (base == null) return;
    const currentNode = editor.state.doc.nodeAt(base);
    if (!currentNode || currentNode.type.name !== "tabs") return;
    const children = panelChildrenOf(currentNode);
    if (fromIdx < 0 || toIdx < 0 || fromIdx >= children.length || toIdx >= children.length) return;

    const currentActive = resolveActiveIndex(children, Number(currentNode.attrs.activeIndex ?? 0));
    const reordered = arrayMove(children, fromIdx, toIdx);
    const newActive =
      currentActive === fromIdx
        ? toIdx
        : currentActive > fromIdx && currentActive <= toIdx
          ? currentActive - 1
          : currentActive < fromIdx && currentActive >= toIdx
            ? currentActive + 1
            : currentActive;

    preserveScroll(() => {
      replaceTabsChildren(base, currentNode, reordered, newActive, true);
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const fromIdx = Number(String(active.id).split(":")[1]);
    const toIdx = Number(String(over.id).split(":")[1]);
    if (Number.isNaN(fromIdx) || Number.isNaN(toIdx)) return;
    reorderTabs(fromIdx, toIdx);
  };

  const ids = panels.map((p) => `tab:${p.index}`);

  return (
    <NodeViewWrapper as="div" className="kb-tabs" data-type="tabs">
      <div className="kb-tabs__bar" contentEditable={false} role="tablist" data-kb-editor-ui="true">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
            {panels.map((p) => (
              <TabLabel
                key={`${p.index}-${p.label}`}
                id={`tab:${p.index}`}
                index={p.index}
                label={p.label}
                active={p.index === visualActiveIndex}
                editing={editingIdx === p.index}
                editable={editable}
                onActivate={() => setActive(p.index)}
                onStartRename={() => setEditingIdx(p.index)}
                onCommitRename={(v) => {
                  renameTab(p.index, v);
                  setEditingIdx(null);
                }}
                onCancelRename={() => setEditingIdx(null)}
                onDuplicate={() => duplicateTab(p.index)}
                onDelete={() => deleteTab(p.index)}
                canDelete={node.childCount > 1}
              />
            ))}
          </SortableContext>
        </DndContext>

        {editable && (
          <button
            type="button"
            className="kb-tabs__add"
            title="Adicionar aba"
            onMouseDown={(e) => e.preventDefault()}
            onClick={addTab}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="kb-tabs__body">
        <NodeViewContent />
      </div>
    </NodeViewWrapper>
  );
}

// ---------------------------------------------------------------------------
// TabLabel — label individual (draggable + inline rename + menu)
// ---------------------------------------------------------------------------
interface TabLabelProps {
  id: string;
  index: number;
  label: string;
  active: boolean;
  editing: boolean;
  editable: boolean;
  canDelete: boolean;
  onActivate: () => void;
  onStartRename: () => void;
  onCommitRename: (value: string) => void;
  onCancelRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function TabLabel({
  id, index, label, active, editing, editable, canDelete,
  onActivate, onStartRename, onCommitRename, onCancelRename,
  onDuplicate, onDelete,
}: TabLabelProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editable || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as const;

  const [draft, setDraft] = useState(label);
  useEffect(() => { setDraft(label); }, [label, editing]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kb-tabs__label-wrap"
      data-active={active ? "true" : "false"}
    >
      <button
        type="button"
        role="tab"
        aria-selected={active}
        className="kb-tabs__label"
        onClick={onActivate}
        onDoubleClick={() => editable && onStartRename()}
        onMouseDown={(e) => {
          // dnd-kit precisa do pointerdown para ativar drag
          if (editing) e.stopPropagation();
        }}
        {...(editable && !editing ? listeners : {})}
        {...attributes}
      >
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => onCommitRename(draft)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); onCommitRename(draft); }
              else if (e.key === "Escape") { e.preventDefault(); onCancelRename(); }
            }}
            className="kb-tabs__label-input"
          />
        ) : (
          <span className="kb-tabs__label-text">{label}</span>
        )}
      </button>

      {editable && !editing && (
        <TabActionsMenu
          canDelete={canDelete}
          onStartRename={onStartRename}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabActionsMenu — menu "..." de ações da aba.
// Controlado por estado local para garantir abertura mesmo dentro do
// DndContext do editor (que faria setPointerCapture no pointerdown).
// ---------------------------------------------------------------------------
interface TabActionsMenuProps {
  canDelete: boolean;
  onStartRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function TabActionsMenu({ canDelete, onStartRename, onDuplicate, onDelete }: TabActionsMenuProps) {
  const [open, setOpen] = useState(false);

  // Fecha o menu ANTES de invocar a ação — garante unmount limpo do Content
  // e evita que o item seja re-renderizado enquanto o handler ainda executa.
  const runAndClose = (fn: () => void) => {
    setOpen(false);
    // Micro-defer para o Radix desmontar o Content antes da mutação do doc.
    queueMicrotask(fn);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="kb-tabs__menu-btn"
          title="Opções da aba"
          data-kb-editor-ui="true"
          // Bloqueia o PointerSensor externo (dnd-kit do KbTiptapEditor) de
          // capturar o gesto e impedir o click de completar no botão.
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-kb-editor-ui="true"
        // Impede o ProseMirror de roubar o foco (que fecharia o menu no ato).
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); runAndClose(onStartRename); }}>
          <Pencil className="h-4 w-4 mr-2" /> Renomear
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); runAndClose(onDuplicate); }}>
          <Copy className="h-4 w-4 mr-2" /> Duplicar
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canDelete}
          onSelect={(e) => { e.preventDefault(); runAndClose(onDelete); }}
          className="text-destructive focus:text-destructive"
        >
          <X className="h-4 w-4 mr-2" /> Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
